import {
  TimeUnit,
  TypeGuard,
  getEnumValues,
  isEnumValue,
  isISODateType,
  isObject,
  type INamedDocument,
  type ISODateType,
  type ISoftDelete,
} from '@principle-theorem/shared';
import { isArray, isString } from 'lodash';
import { IFeeSchedule } from '../clinical-charting/fees/fee-schedule/fee-schedule';
import { IStatusHistory } from '../common';
import { INote } from '../note/note';
import { IPractice } from '../practice/practice';
import { IChartSettings, IStaffer } from '../staffer/staffer';
import { ITag } from '../tag/tag';
import { IAccountSummary } from './account-summary';
import {
  IDVACard,
  IHealthFundCard,
  IHealthInsuranceDetails,
  IMedicareCard,
} from './healthfund-cards';
import { IPatientMetadata } from './patient-metadata';
import {
  isPatientRelationship,
  type IPatientRelationship,
} from './patient-relationship';
import { IPatientRecall } from './recalls';
import { type IReferralSource } from './referral-source';

export const NICKNAME_REGEX = new RegExp(/\(.*?\)/g);

export class PatientContactDetailsError extends Error {}

export interface IDisplayAge {
  value: number;
  unit: TimeUnit;
  secondaryValue?: number;
  secondaryUnit?: TimeUnit;
}

export enum Gender {
  Male = 'male',
  Female = 'female',
  Other = 'other',
  NotSpecified = 'notSpecified',
}

export const GENDERS: Gender[] = getEnumValues(Gender);

export enum PatientEventAction {
  Activated = 'activated',
  Blocked = 'blocked',
  Deactivated = 'deactivated',
}

export enum PatientStatus {
  Lead = 'lead',
  Active = 'active',
  Blocked = 'blocked',
  Inactive = 'inactive',
  Deceased = 'deceased',
}

export const PATIENT_STATUSES: PatientStatus[] = getEnumValues(PatientStatus);

/**
 * Patient statuses eligible to be matched against an inbound message.
 *
 * Order is significant - it doubles as the ranking used to choose between
 * patients sharing a phone number, so do not reorder this list.
 *
 * Only `Blocked` is excluded, because blocking is an explicit instruction that
 * the practice does not want to hear from that patient. Every other status is
 * matched so a reply is never silently dropped without the practice knowing.
 */
export const INBOUND_MESSAGE_PATIENT_STATUSES: PatientStatus[] = [
  PatientStatus.Active,
  PatientStatus.Lead,
  PatientStatus.Inactive,
  PatientStatus.Deceased,
];

export interface IPatientSettings {
  charting: Partial<IChartSettings>;
}

export enum PatientFormType {
  PatientDetailsForm = 'patient-details-form',
  CustomForm = 'custom-form',
}

export enum PatientFormSpecialUid {
  MedicalHistoryForm = 'medical-history-form',
  PatientDetailsForm = 'patient-details-form',
}

export enum AccPatientFormType {
  AccPatientDetailsForm = 'acc-patient-details-form',
  AccAccidentDetailsForm = 'acc-accident-details-form',
  AccEmploymentDetailsForm = 'acc-employment-details-form',
}

export const PATIENT_FORM_NAME: { [key in PatientFormSpecialUid]: string } = {
  [PatientFormSpecialUid.MedicalHistoryForm]: 'Medical History Form',
  [PatientFormSpecialUid.PatientDetailsForm]: 'Patient Details Form',
};

export const PATIENT_FORM_NAME_MAP: {
  [key in PatientFormType | AccPatientFormType]: string;
} = {
  [AccPatientFormType.AccPatientDetailsForm]: 'ACC Patient Details Form',
  [AccPatientFormType.AccAccidentDetailsForm]: 'ACC Accident Details Form',
  [AccPatientFormType.AccEmploymentDetailsForm]: 'ACC Employment Details Form',
  [PatientFormType.PatientDetailsForm]: 'Patient Details Form',
  [PatientFormType.CustomForm]: 'Custom Form',
};

export interface IHasPrimaryContact {
  primaryContact: IPatientRelationship<IBasePatient & IsPrimaryContact>;
}

export enum ContactNumberLabel {
  Mobile = 'Mobile',
  Home = 'Home',
  Work = 'Work',
}

export interface IContactNumber {
  label?: string;
  number: string;
}

export function isContactNumber(data: unknown): data is IContactNumber {
  return isObject(data) && isString(data.label) && isString(data.number);
}

interface IBasePatientContactDetails {
  address: string;
}

interface IPatientEmailContactDetails extends IBasePatientContactDetails {
  email: string;
  contactNumbers?: IContactNumber[];
}

interface IPatientPhoneContactDetails extends IBasePatientContactDetails {
  contactNumbers: IContactNumber[];
  email?: string;
}

export type IPatientContactDetails =
  | IPatientEmailContactDetails
  | IPatientPhoneContactDetails;

export function isPatientContactDetails(
  data: unknown
): data is IPatientContactDetails {
  return (
    isObject(data) &&
    isString(data.address) &&
    (isString(data.email) ||
      TypeGuard.arrayOf(isContactNumber)(data.contactNumbers))
  );
}

export type WithPrimaryContact = Partial<IPatientContactDetails> &
  IHasPrimaryContact;
export type WithContactDetails = IPatientContactDetails &
  Partial<IHasPrimaryContact>;
export type IsPrimaryContact = IPatientContactDetails & {
  isPrimaryContact: true;
};

export interface IBasePatient extends ISoftDelete {
  name: string;
  referrer?: IReferralSource;
  relationships: IPatientRelationship[];
  dateOfBirth?: ISODateType;
  gender: Gender;
  status: PatientStatus;
  statusHistory: IStatusHistory<PatientStatus>[];
  preferredDentist?: INamedDocument<IStaffer>;
  preferredHygienist?: INamedDocument<IStaffer>;
  preferredPractice?: INamedDocument<IPractice>;
  preferredFeeSchedule?: INamedDocument<IFeeSchedule>;
  notes: INote[];
  tags: INamedDocument<ITag>[];
  accountSummary: IAccountSummary;
  profileImageURL?: string;
  medicareCard?: IMedicareCard;
  healthFundCard?: IHealthFundCard;
  dvaCard?: IDVACard;
  nationalHealthIndexNumber?: string; // NHI - National Health Index Number (New Zealand)
  healthInsuranceDetails?: IHealthInsuranceDetails;
  referenceId?: string;
  settings?: Partial<IPatientSettings>;
  metadata?: IPatientMetadata;
  recall: IPatientRecall;
  secondaryRecall?: IPatientRecall;
  depositRequired?: boolean;
}

export type IPatient = IBasePatient &
  (WithPrimaryContact | WithContactDetails | IsPrimaryContact);

export function isPatient(data: unknown): data is IPatient {
  const canContact = isWithPrimaryContact(data) || isWithContactDetails(data);
  return (
    isObject(data) &&
    'name' in data &&
    isString(data.name) &&
    TypeGuard.undefinedOr(isISODateType)(data.dateOfBirth) &&
    'gender' in data &&
    isEnumValue(Gender, data.gender) &&
    'status' in data &&
    isEnumValue(PatientStatus, data.status) &&
    'tags' in data &&
    isArray(data.tags) &&
    canContact
  );
}

export function isWithPrimaryContact(
  data: unknown
): data is WithPrimaryContact {
  return (
    isObject(data) &&
    'primaryContact' in data &&
    isPatientRelationship(data.primaryContact)
  );
}

export function isPatientWithPrimaryContact(
  data: unknown
): data is IPatient & WithPrimaryContact {
  return isPatient(data) && isWithPrimaryContact(data);
}

export function isWithContactDetails(
  data: unknown
): data is WithContactDetails {
  return (
    isObject(data) &&
    isString(data.address) &&
    (isString(data.email) ||
      (isArray(data.contactNumbers) &&
        data.contactNumbers.every(isContactNumber)))
  );
}

export function isPatientWithContactDetails(
  data: unknown
): data is IPatient & WithContactDetails {
  return isPatient(data) && isWithContactDetails(data);
}

export function isPrimaryContactPatient(
  data: unknown
): data is IBasePatient & IsPrimaryContact {
  return (
    isPatientWithContactDetails(data) &&
    'isPrimaryContact' in data &&
    data.isPrimaryContact === true
  );
}

export enum PatientCollection {
  Appointments = 'appointments',
  TreatmentPlans = 'treatmentPlans',
  History = 'history',
  Invoices = 'invoices',
  Credits = 'credits',
  PaymentPlans = 'paymentPlans',
  SubmittedForms = 'submittedForms',
  ClinicalCharts = 'clinicalCharts',
  ClinicalNotes = 'clinicalNotes',
  Media = 'media',
  PatientDocuments = 'patientDocuments',
  Automations = 'patientAutomations',
  Prescriptions = 'prescriptions',
  PatientDetailsHistory = 'patientDetailsHistory',
  ScribeSessions = 'scribeSessions',
}
